import { message } from 'antd';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import { FormActionsEntity } from 'entities/FormActions';
import {
  FormActionsRequest,
  FormActionsResponse,
} from 'entities/endpoints/form.actions';
import { FormActionsExecuteRequest } from 'entities/endpoints/form.actions.execute';
import { ApiRequest } from 'stores/common/ApiRequest';
import { ILocalStore } from 'stores/interfaces';

import { ITableFactoryStore } from '../../table';

import { FORM_ACTIONS_TEXTS } from './texts';

type PrivateFields = '_actions';

type FormActionsStoreParams = {
  store: ITableFactoryStore;
};

class FormActionsStore implements ILocalStore {
  constructor({ store }: FormActionsStoreParams) {
    this._tableStore = store;
    makeObservable<FormActionsStore, PrivateFields>(this, {
      _actions: observable.ref,
      actions: computed,

      fetchActions: action.bound,
    });
  }

  private readonly _tableStore: ITableFactoryStore;
  private _actions: FormActionsEntity[] = [];

  readonly formActionsGetRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.actions,
    methodType: 'GET',
  });

  readonly formActionExecuteRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.actionsExecute,
    methodType: 'POST',
  });

  get actions() {
    return this._actions;
  }

  async executeAction(params: FormActionsEntity): Promise<void> {
    const { kind, extra } = params;

    const formId = this._tableStore.callParams?.form_id;

    const rootRecordId = this._tableStore.header?.id;

    if (!formId || !rootRecordId) {
      return;
    }

    const { isError } = await this.formActionExecuteRequest.call<
      null,
      FormActionsExecuteRequest
    >({
      data: {
        extra,
        kind,
        id: formId,
        root_record_id: rootRecordId,
      },
    });

    if (isError) {
      message.error(FORM_ACTIONS_TEXTS.SAVE_ERROR);

      return;
    }

    /* todo: поддержать получение новых кнопок при запросе на execute, когда будет готов бэк */
    await Promise.all([this._tableStore.refresh(), this.fetchActions()]);

    message.success(FORM_ACTIONS_TEXTS.SAVE_SUCCESS);
  }

  async fetchActions(): Promise<void> {
    const formId = this._tableStore.callParams?.form_id;

    const rootRecordId = this._tableStore.header?.id;

    if (!formId || !rootRecordId) {
      return;
    }

    const { isError, data } = await this.formActionsGetRequest.call<
      FormActionsResponse,
      FormActionsRequest
    >({
      data: {
        id: formId,
        root_record_id: rootRecordId,
      },
    });

    if (isError || !data) {
      return;
    }

    runInAction(() => {
      this._actions = data.actions;
    });
  }

  destroy() {
    this.formActionsGetRequest.destroy();
    this.formActionExecuteRequest.destroy();
  }
}

export default FormActionsStore;
